import { cloneDeep, find, get, last, first } from 'lodash';
import {
  Matcher,
  QuestionnaireConditionContext,
  questionnaireLogicService,
  statementService,
} from 'utils/adl-services';
import {
  updateState,
  transformNodeToQlQuestionnaireNode,
} from 'containers/QuestionnaireLogic/utils';

import {
  initialState as initialQuestionnaireState,
  fetchQuestionnaireSuccessActionHandler,
  addQuestionToQuestionnaireActionHandler,
  updateQuestionnaireActionHandler,
  moveQuestionActionHandler,
} from 'containers/QuestionnairePage/reducer';
import { setAnswerScreenerTypeHandler } from 'containers/QuestionnairePage/reducerHandlers/screenerTypes';
import { setAnswerScreenerType } from 'containers/QuestionnairePage/actions';
import {
  addStatementActionHandler,
  updateStatementActionHandler,
  addRuleActionHandler,
  updateRuleOperatorActionHandler,
} from 'containers/QuestionnairePage/reducer_ADL_utils';

import {
  getDefaultStatementByQuestionRefId,
  getNonDefaultStatementsByQuestionRefId,
  addScreeningQuestionToQuestionnaireActionHandler,
  updateScreeningQuestionInQuestionnaireActionHandler,
  abAddConceptActionHandler,
  abUpdateConceptActionHandler,
  abUpdatePropertiesActionHandler,
  abRemoveConceptActionHandler,
  abAddDefaultNodesActionHandler,
  groupUpdateAttributeActionHandler,
  groupAddAttributeActionHandler,
  groupUpdateAttributeValueActionHandler,
  groupRemoveAttributeActionHandler,
  removeQuestionFromQuestionnaireActionHandler,
  vanWestendorpToggleLikelihoodNodesActionHandler,
  vanWestendorpUpdateSettingsHandler,
  removeAllScreeningQuestionFromQuestionnaireActionHandler,
} from 'containers/QuestionnairePage/reducer_utils';

// Some survey types (e.g. Peeq) pre-seed screening questions into
// surveyDetailsExtended.questions at fetch time, while the questionnaire logic
// (ql) is built only from the non-screening nodes. That leaves the ql without a
// nodeStatements entry for those screening nodes. Later operations that rebuild
// ql.questionnaire.nodes from questions[] would then reference a statement-less
// node and throw "NodeStatements for node ... was not found". Adding a default
// passthrough for each such node keeps questionnaire.nodes and nodeStatements
// consistent before any generator operation runs.
export function reconcileMissingNodeStatements(state) {
  const ql = get(state, 'questionnaireLogic.ql');
  const questions = get(state, 'surveyDetailsExtended.questions', []);

  if (!ql || !Array.isArray(ql.nodeStatements)) {
    return state;
  }

  const hasNodeStatements = (currentQl, refId) =>
    (currentQl.nodeStatements || []).some(({ nodeId }) => nodeId === refId);

  const hasMissing = questions.some(
    (node) => !hasNodeStatements(ql, node.refId),
  );
  if (!hasMissing) {
    return state;
  }

  let newQl = ql;
  questions.forEach((node, index) => {
    if (hasNodeStatements(newQl, node.refId)) {
      return;
    }
    const transformedNode = transformNodeToQlQuestionnaireNode({ node });
    newQl = questionnaireLogicService.addNode(
      newQl,
      transformedNode,
      index,
      undefined,
      undefined,
      node.groupRefId,
    );
  });

  const newQuestionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic: state.questionnaireLogic,
    shouldUpdateVersion: false,
  });

  return {
    ...state,
    questionnaireLogic: newQuestionnaireLogic,
  };
}

class QuestionnaireAPI {
  // using a private member here
  // to avoid accidental external mutations
  #state;

  constructor(fetchedQuestionnaire = { questions: [] }) {
    const defaultFetchedData = {
      payload: fetchedQuestionnaire,
    };
    const initializedState = fetchQuestionnaireSuccessActionHandler(
      initialQuestionnaireState,
      defaultFetchedData,
    );

    this.#state = reconcileMissingNodeStatements(initializedState);
  }

  _getQuestion(refId) {
    const questions = get(this.#state, 'surveyDetailsExtended.questions', []);
    const question = find(questions, ['refId', refId]);
    return question;
  }

  _getQuestionCount() {
    const questions = get(this.#state, 'surveyDetailsExtended.questions', []);
    return questions.length;
  }

  addQuestion(question) {
    const updatedState = addQuestionToQuestionnaireActionHandler(this.#state, {
      question,
    });

    this.#state = updatedState;
  }

  updateQuestion(question) {
    const updatedState = updateQuestionnaireActionHandler(this.#state, {
      question,
    });

    this.#state = updatedState;
  }

  setAnswerScreenerType(payload) {
    this.#state = setAnswerScreenerTypeHandler(
      this.#state,
      setAnswerScreenerType(payload),
    );
  }

  removeQuestion({ refId, groupRefId }) {
    const updatedState = removeQuestionFromQuestionnaireActionHandler(
      this.#state,
      {
        payload: {
          refId,
          groupRefId,
        },
      },
    );

    this.#state = updatedState;
  }

  moveQuestion(question, direction) {
    const updatedState = moveQuestionActionHandler(this.#state, {
      payload: {
        refId: question.refId,
        direction,
      },
    });

    this.#state = updatedState;
  }

  addAbConcept(abRefId) {
    const updatedState = abAddConceptActionHandler(this.#state, {
      payload: { refId: abRefId },
    });

    this.#state = updatedState;

    //  find the added concept refId and return it
    const questions = get(this.#state, 'surveyDetailsExtended.questions', []);
    const abQuestion = find(questions, ['refId', abRefId]);
    if (abQuestion) {
      const abConcepts = get(abQuestion, 'concepts', []);
      const lastConcept = last(abConcepts);
      if (lastConcept) {
        return lastConcept.refId;
      }
    }
    return undefined;
  }

  updateAbConcept({ refId, conceptRefId, name }) {
    const updatedState = abUpdateConceptActionHandler(this.#state, {
      payload: {
        refId,
        conceptRefId,
        name,
      },
    });

    this.#state = updatedState;
  }

  addAbDefaultNodes({ refId, type }) {
    const updatedState = abAddDefaultNodesActionHandler(this.#state, {
      payload: {
        refId,
        type,
      },
    });

    this.#state = updatedState;
  }

  removeAbConcept({ refId, conceptRefId }) {
    const updatedState = abRemoveConceptActionHandler(this.#state, {
      payload: {
        refId,
        conceptRefId,
      },
    });

    this.#state = updatedState;
  }

  updateAbProperties({ refId, properties }) {
    // properties: { repetitions }
    const updatedState = abUpdatePropertiesActionHandler(this.#state, {
      payload: {
        refId,
        properties,
      },
    });

    this.#state = updatedState;
  }

  addGroupAttribute({ refId, name, type, groupType }) {
    const updatedState = groupAddAttributeActionHandler(this.#state, {
      payload: {
        refId,
        name,
        type,
        groupType,
      },
    });

    this.#state = updatedState;

    //  find the added attribute and return a copy of it
    const questions = get(this.#state, 'surveyDetailsExtended.questions', []);
    const groupQuestion = find(questions, ['refId', refId]);
    if (groupQuestion) {
      const groupAttributes = get(groupQuestion, 'attributes', []);
      const lastAttribute = last(groupAttributes);
      if (lastAttribute) {
        return cloneDeep(lastAttribute);
      }
    }
    return undefined;
  }

  updateGroupAttribute({ refId, attributeRefId, name, ...restPayload }) {
    const updatedState = groupUpdateAttributeActionHandler(this.#state, {
      payload: {
        refId,
        attributeRefId,
        name,
        ...restPayload,
      },
    });

    this.#state = updatedState;
  }

  updateGroupAttributeValue({
    refId,
    attributeValueRefId,
    attributeRefId,
    text,
    media,
  }) {
    const updatedState = groupUpdateAttributeValueActionHandler(this.#state, {
      payload: {
        refId,
        attributeValueRefId,
        attributeRefId,
        text,
        media,
      },
    });

    this.#state = updatedState;
  }

  removeGroupAttribute({ refId, attributeRefId, groupType }) {
    const updatedState = groupRemoveAttributeActionHandler(this.#state, {
      payload: {
        refId,
        attributeRefId,
        groupType,
      },
    });

    this.#state = updatedState;
  }

  addScreeningQuestion(screeningQuestion) {
    const updatedState = addScreeningQuestionToQuestionnaireActionHandler(
      this.#state,
      {
        payload: screeningQuestion,
      },
    );

    this.#state = updatedState;
  }

  updateScreeningQuestion(screeningQuestion) {
    const updatedState = updateScreeningQuestionInQuestionnaireActionHandler(
      this.#state,
      {
        payload: { question: screeningQuestion },
      },
    );

    this.#state = updatedState;
  }

  removeAllScreeningQuestions() {
    const updatedState =
      removeAllScreeningQuestionFromQuestionnaireActionHandler(this.#state);
    this.#state = updatedState;
  }

  addStatement(statement) {
    const { state: updatedState, statementId } = addStatementActionHandler(
      this.#state,
      {
        payload: statement,
      },
    );

    this.#state = updatedState;
    return statementId;
  }

  updateStatementOutcome(statement) {
    const updatedState = updateStatementActionHandler(this.#state, {
      payload: statement,
    });

    this.#state = updatedState;
  }

  addRule(question, answer, context) {
    // works with single Rule for the moment
    const statement = getNonDefaultStatementsByQuestionRefId(
      this.#state,
      question.refId,
    );
    const rule = {
      nodeRefId: question.refId,
      statementId: statement.id,
      matcher: Matcher.Equal,
      contextType: QuestionnaireConditionContext.Answer,
      context,
      defaultValue: answer.refId,
    };
    const updatedState = addRuleActionHandler(this.#state, {
      payload: rule,
    });

    this.#state = updatedState;
  }

  addRuleToStatement({ question, statementId, matcher, answer, context }) {
    const rule = {
      nodeRefId: question.refId,
      statementId,
      matcher,
      contextType: QuestionnaireConditionContext.Answer,
      context,
      defaultValue: answer.refId,
    };
    const updatedState = addRuleActionHandler(this.#state, {
      payload: rule,
    });

    this.#state = updatedState;
  }

  updateRuleOperatorOfStatement(question, statementId, operator) {
    const qStatements = questionnaireLogicService.findNodeStatementsByNodeId(
      this.#state.questionnaireLogic.ql,
      question.refId,
    );
    const statement = find(qStatements.statements, ['id', statementId]);
    const rules = statementService.getAllLeafRules(statement);
    const parentNode = questionnaireLogicService.getParentNode(
      this.#state.questionnaireLogic.ql,
      question.refId,
      first(rules),
    );

    const actionPayload = {
      nodeRefId: question.refId,
      statementId: statement.id,
      ruleId: parentNode.id,
      value: operator,
    };
    const updatedState = updateRuleOperatorActionHandler(this.#state, {
      payload: actionPayload,
    });

    this.#state = updatedState;
  }

  updateRuleOperator(question, operator) {
    // works with single Rule for the moment
    const statement = getNonDefaultStatementsByQuestionRefId(
      this.#state,
      question.refId,
    );
    const { rule } = statement;
    const actionPayload = {
      nodeRefId: question.refId,
      statementId: statement.id,
      ruleId: rule.id,
      value: operator,
    };
    const updatedState = updateRuleOperatorActionHandler(this.#state, {
      payload: actionPayload,
    });

    this.#state = updatedState;
  }

  setQuestionDefaultOutcome(sourceQuestion, targetQuestion) {
    const { refId: sourceRefId } = sourceQuestion;
    const { refId: targetRefId } = targetQuestion;

    const statement = getDefaultStatementByQuestionRefId(
      this.#state,
      sourceRefId,
    );

    if (statement) {
      this.updateStatementOutcome({
        nodeRefId: sourceRefId,
        statementId: statement.id,
        outcome: {
          params: { id: targetRefId },
        },
      });
    }
  }

  linkAnswerToQuestion({
    question,
    answerRefId,
    targetQuestion,
    context,
    matcher = Matcher.Equal,
  }) {
    const statementAction = {
      nodeRefId: question.refId,
      matcher,
      answerRefId,
      nextQuestionRefId: targetQuestion.refId,
      context,
      contextType: QuestionnaireConditionContext.Answer,
    };
    return this.addStatement(statementAction);
  }

  updateVanWestendorpLikelihood(refId, enableLikelihood) {
    const updatedState = vanWestendorpToggleLikelihoodNodesActionHandler(
      this.#state,
      {
        payload: { refId, enableLikelihood },
      },
    );
    this.#state = updatedState;
  }

  updateVanWestendorpSettings({ refId, settings }) {
    this.#state = vanWestendorpUpdateSettingsHandler(this.#state, {
      payload: { refId, settings },
    });
  }

  get state() {
    return cloneDeep(this.#state);
  }
}

export default QuestionnaireAPI;
